import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  api,
  OnboardingProgress,
  Permission,
  useAuthBootstrap,
  useAuthStore,
  usePermission,
  usePermissionsStatus,
  usePermissions,
  useTenantWidePermission,
  useThemeStore,
  useVerticalConfig,
  useVerticalModule,
} from '@lipaflow/shared'
import { LockClosedIcon } from '@heroicons/react/24/outline'

// Layouts
import DashboardLayout from './components/layouts/DashboardLayout'
import { useClearCacheOnAccountChange } from './lib/sessionCache'
import OnboardingLayout from './components/layouts/OnboardingLayout'
import { OnboardingErrorBoundary } from './components/ErrorBoundary'
import { VerticalMismatchGuard } from './components/VerticalMismatchGuard'
import {
  isAdminAssistedSetupPending,
  isDemoTenant,
  shouldBlockNonAdminStaff,
  shouldUseSelfServiceOnboarding,
} from './lib/onboardingRouting'

// Pages – lazy loaded for code-splitting
const LoginPage = lazy(() => import('./pages/LoginPage'))
const InvitePage = lazy(() => import('./pages/InvitePage'))
const SignupPage = lazy(() => import('./pages/SignupPage'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const ClientsPage = lazy(() => import('./pages/ClientsPage'))
const BeneficiariesPage = lazy(() => import('./pages/BeneficiariesPage'))
const ApprovalsPage = lazy(() => import('./pages/ApprovalsPage'))
const SponsorshipsPage = lazy(() => import('./pages/SponsorshipsPage'))
const DepositsToAssignPage = lazy(() => import('./pages/DepositsToAssignPage'))
const OpeningBalancesPage = lazy(() => import('./pages/OpeningBalancesPage'))
const SessionsPage = lazy(() => import('./pages/SessionsPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const AccountPage = lazy(() => import('./pages/AccountPage'))
const PaymentPlansPage = lazy(() => import('./pages/PaymentPlansPage'))
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'))
const ActivationPage = lazy(() => import('./pages/onboarding/ActivationPage'))
const PledgesPage = lazy(() => import('./pages/PledgesPage'))
const WaiversPage = lazy(() => import('./pages/WaiversPage'))
const PaymentsReceivedPage = lazy(() => import('./pages/PaymentsReceivedPage'))
const EscalationsPage = lazy(() => import('./pages/EscalationsPage'))
const SegmentsPage = lazy(() => import('./pages/SegmentsPage'))
const TemplatesPage = lazy(() => import('./pages/TemplatesPage'))
const BroadcastsPage = lazy(() => import('./pages/BroadcastsPage'))
// Rental operator pages now live in @lipaflow/rental (Tier 2). This shell
// still composes both verticals -- the interim state ADR-0004 records, deleted
// when the app splits into school-portal and property-portal.
const AuditTrailPage = lazy(() => import('./pages/AuditTrailPage'))
const ReportsPage = lazy(() => import('./pages/ReportsPage'))
const InsightsPage = lazy(() => import('./pages/InsightsPage'))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'))
const PaymentReturnPage = lazy(() => import('./pages/PaymentReturnPage'))
const PayInvoicePage = lazy(() => import('./pages/PayInvoicePage'))
const SupportSessionPage = lazy(() => import('./pages/SupportSessionPage'))
const OnboardingStart = lazy(() => import('./pages/onboarding/OnboardingStart'))

// Onboarding – dynamic step loader
import StepLoader from './pages/onboarding/StepLoader'
import { collects } from './lib/features'

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
    </div>
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const { isValidating } = useAuthBootstrap()

  if (isValidating) {
    return <PageLoader />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

/** The API did not answer, so we do not know what this person may see. */
function CannotReach({ fullScreen = false }: { fullScreen?: boolean }) {
  return (
    <div className={`${fullScreen ? 'min-h-screen bg-stone-50' : 'min-h-[60vh]'} flex items-center justify-center p-4`}>
      <div role="alert" className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
        <h2 className="text-xl font-semibold text-stone-900">Can&apos;t reach LipaFlow right now</h2>
        <p className="mt-2 text-sm text-stone-600">Check your connection. We&apos;ll keep trying.</p>
      </div>
    </div>
  )
}

function PermissionRoute({
  children,
  permission,
  permissions,
  tenantWide = false,
}: {
  children: React.ReactNode
  permission?: Permission
  permissions?: Permission[]
  tenantWide?: boolean
}) {
  const hasSinglePermission = usePermission(permission as Permission)
  const hasTenantWidePermission = useTenantWidePermission(permission as Permission)
  const hasAllPermissions = usePermissions(permissions || [])
  const permissionsStatus = usePermissionsStatus()
  const isAllowed = permission ? (tenantWide ? hasTenantWidePermission : hasSinglePermission) : hasAllPermissions

  // Not known yet (loading, or the API did not answer): never tell someone
  // they lack access because a request failed.
  if (!isAllowed && permissionsStatus === 'loading') return <PageLoader />
  if (!isAllowed && permissionsStatus === 'unavailable') return <CannotReach />

  if (!isAllowed) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 text-center">
        <LockClosedIcon className="w-10 h-10 text-warning-500 mb-3" />
        <h2 className="text-lg font-semibold text-stone-900">Access Restricted</h2>
        <p className="text-sm text-stone-500 mt-1">
          You do not have permission to view this page.
        </p>
      </div>
    )
  }

  return <>{children}</>
}

function OnboardingPendingScreen({
  adminAssisted = false,
}: {
  adminAssisted?: boolean
}) {
  const { labels } = useVerticalConfig()
  const institutionLabel = labels.institution || 'Organization'

  return (
    <div className="min-h-screen flex items-center justify-center bg-cloud-100 p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-card border border-stone-100 p-8 text-center animate-fade-in">
        <div className="w-16 h-16 mx-auto mb-4 bg-teal-50 rounded-full flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
        </div>
        <h2 className="font-outfit text-h3 text-stone-800 mb-2">
          {adminAssisted ? 'Setup In Progress' : `Setting Up Your ${institutionLabel}`}
        </h2>
        <p className="font-inter text-body text-stone-500">
          {adminAssisted
            ? `The LipaFlow team is still completing this ${institutionLabel.toLowerCase()} setup. You will be able to access the portal once setup is finished.`
            : 'Your administrator is still completing the initial setup. You will be able to access the portal once setup is finished.'}
        </p>
      </div>
    </div>
  )
}

function OnboardingGuard({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const user = useAuthStore((state) => state.user)
  const tenant = useAuthStore((state) => state.tenant)
  const canManageUsers = usePermission('users:manage')

  const isDemo = isDemoTenant(tenant)

  const { data: progress, isLoading } = useQuery<OnboardingProgress>({
    queryKey: ['onboarding-progress-gate', user?.tenant_id],
    queryFn: async () => {
      const res = await api.get('/onboarding/progress')
      return res.data
    },
    enabled: isAuthenticated && !!user && !isDemo,
    retry: false,
  })

  if (isAuthenticated && user) {
    if (isDemo) {
      return <>{children}</>
    }

    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )
    }

    if (progress && !progress.is_complete) {
      if (isAdminAssistedSetupPending(progress)) {
        return <OnboardingPendingScreen adminAssisted />
      }
      if (shouldUseSelfServiceOnboarding(progress, canManageUsers)) {
        return <Navigate to="/onboarding" replace />
      }
      if (shouldBlockNonAdminStaff(progress, canManageUsers)) {
        return <OnboardingPendingScreen />
      }
    }
  }

  return <>{children}</>
}

function OnboardingRoute() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const user = useAuthStore((state) => state.user)
  const tenant = useAuthStore((state) => state.tenant)
  const canManageUsers = usePermission('users:manage')
  const permissionsStatus = usePermissionsStatus()

  const isDemo = isDemoTenant(tenant)

  const { data: progress, isLoading } = useQuery<OnboardingProgress>({
    queryKey: ['onboarding-progress-route', user?.tenant_id],
    queryFn: async () => {
      const res = await api.get('/onboarding/progress')
      return res.data
    },
    enabled: isAuthenticated && !!user && !isDemo,
    retry: false,
  })

  // Check authentication
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />
  }

  if (isDemo) {
    return <Navigate to="/" replace />
  }

  if (progress?.is_complete) {
    return <Navigate to="/" replace />
  }

  // Permissions not known yet (loading, or the API is unreachable): never
  // tell an administrator they are not one.
  if (permissionsStatus === 'loading') return <PageLoader />
  if (permissionsStatus === 'unavailable') return <CannotReach fullScreen />

  // Check if user is admin
  if (!canManageUsers) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-warning-100 rounded-full flex items-center justify-center">
            <LockClosedIcon className="w-8 h-8 text-warning-600" />
          </div>

          <h2 className="text-2xl font-bold text-stone-900 mb-2">
            Admin Access Required
          </h2>

          <p className="text-stone-600 mb-6">
            Only your organisation's administrators can finish setup.
            Contact your admin to complete the setup process.
          </p>

          <div className="bg-info-50 border border-info-200 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm text-info-900 font-medium mb-2">
              Your current role: <span className="font-bold">{user.role_labels?.join(', ') || 'No active role'}</span>
            </p>
            <p className="text-sm text-info-800">
              If you need admin access, ask your administrator
              or LipaFlow support at support@lipaflow.co.ke
            </p>
          </div>

          <a
            href="/"
            className="inline-block px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium transition"
          >
            Return to Dashboard
          </a>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return <PageLoader />
  }

  if (isAdminAssistedSetupPending(progress)) {
    return <OnboardingPendingScreen adminAssisted />
  }

  return (
    <OnboardingErrorBoundary>
      <OnboardingLayout />
    </OnboardingErrorBoundary>
  )
}

/**
 * The operator application, minus its vertical.
 *
 * `apps/school-portal` and `apps/property-portal` are thin shells that provide
 * a vertical module and render this. Everything here is shared: auth,
 * onboarding, the dashboard, money, engagement, settings.
 */
export function B2BApp() {
  useClearCacheOnAccountChange()
  // Sync dark mode class on initial mount from persisted store
  const isDark = useThemeStore((s) => s.isDark)
  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark)
  }, [isDark])

  const verticalModule = useVerticalModule()
  const { features } = useVerticalConfig()
  const hasCollections = collects(features)
  const verticalRoutes = (verticalModule?.routes ?? [])
    .filter((route) => !route.requiresFeature || features[route.requiresFeature] !== false)
    .map((route) => (
      <Route
        key={route.path}
        path={route.path}
        element={
          route.requiresPermission ? (
            <PermissionRoute permission={route.requiresPermission as Permission}>
              {route.element}
            </PermissionRoute>
          ) : (
            route.element
          )
        }
      />
    ))

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/invite" element={<InvitePage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/support-session" element={<SupportSessionPage />} />

        {/* Onboarding Flow - Admin Only (dynamic step routing) */}
        <Route
          path="/onboarding"
          element={<OnboardingRoute />}
        >
          <Route index element={<OnboardingStart />} />
          <Route path="step/:stepId" element={<StepLoader />} />
        </Route>

        {/* A LipaFlow invoice's pay link: no sign-in, scoped to one invoice (D10) */}
        <Route path="/pay-invoice" element={<PayInvoicePage />} />

        {/* Paystack same-tab callback — must stay reachable mid-onboarding,
            so it lives outside the OnboardingGuard */}
        <Route
          path="/payments/return"
          element={
            <ProtectedRoute>
              <PaymentReturnPage />
            </ProtectedRoute>
          }
        />

        {/* Main Dashboard */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <VerticalMismatchGuard>
                <OnboardingGuard>
                  <DashboardLayout />
                </OnboardingGuard>
              </VerticalMismatchGuard>
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          {/* The composed vertical's pages. This shell knows their paths only
              at runtime, and enforces the feature switch and permission the
              module declares without knowing what either protects. */}
          {verticalRoutes}
          <Route path="clients" element={<PermissionRoute permission="beneficiaries:view"><ClientsPage /></PermissionRoute>} />
          <Route path="entities" element={<PermissionRoute permission="beneficiaries:view"><BeneficiariesPage /></PermissionRoute>} />
          <Route path="subscriptions" element={<PermissionRoute permission="settings:view"><SubscriptionPage /></PermissionRoute>} />
          <Route path="activation" element={<PermissionRoute permission="settings:view"><ActivationPage /></PermissionRoute>} />
          {/* Money: not part of a plan that collects nothing through LipaFlow
              (a CBC-only school), and refused by the server there too. */}
          {hasCollections && (
            <>
              <Route path="approvals" element={<PermissionRoute permission="payments:view" tenantWide><ApprovalsPage /></PermissionRoute>} />
              <Route path="sponsorships" element={<PermissionRoute permission="payments:view" tenantWide><SponsorshipsPage /></PermissionRoute>} />
              <Route path="deposits-to-assign" element={<PermissionRoute permission="payments:view" tenantWide><DepositsToAssignPage /></PermissionRoute>} />
              <Route path="opening-balances" element={<PermissionRoute permission="payments:view" tenantWide><OpeningBalancesPage /></PermissionRoute>} />
              <Route path="payment-plans" element={<PermissionRoute permission="plans:view"><PaymentPlansPage /></PermissionRoute>} />
              <Route path="pledges" element={<PermissionRoute permission="pledges:view"><PledgesPage /></PermissionRoute>} />
              {/* Asking and approving are separate permissions; the page checks for either. */}
              <Route path="waivers" element={<WaiversPage />} />
              <Route path="payments-received" element={<PermissionRoute permission="payments:view"><PaymentsReceivedPage /></PermissionRoute>} />
              <Route path="escalations" element={<PermissionRoute permission="escalations:view"><EscalationsPage /></PermissionRoute>} />
              <Route path="segments" element={<PermissionRoute permission="segments:view"><SegmentsPage /></PermissionRoute>} />
              <Route path="conversations" element={<PermissionRoute permission="sessions:view"><SessionsPage /></PermissionRoute>} />
              <Route path="reports" element={<PermissionRoute permission="reports:view"><ReportsPage /></PermissionRoute>} />
              <Route path="insights" element={<PermissionRoute permission="reports:view" tenantWide><InsightsPage /></PermissionRoute>} />
            </>
          )}
          <Route path="templates" element={<PermissionRoute permission="broadcasts:view"><TemplatesPage /></PermissionRoute>} />
          <Route path="broadcasts" element={<PermissionRoute permission="broadcasts:view"><BroadcastsPage /></PermissionRoute>} />
          <Route path="audit-trail" element={<PermissionRoute permission="audit:view"><AuditTrailPage /></PermissionRoute>} />
          {/* Everyone's own sign-in: no permission, so any staff member can link their phone. */}
          <Route path="account" element={<AccountPage />} />
          <Route path="settings" element={<PermissionRoute permission="settings:view"><SettingsPage /></PermissionRoute>} />
          <Route path="settings/:tab" element={<PermissionRoute permission="settings:view"><SettingsPage /></PermissionRoute>} />
          {/* Rental-specific routes (feature-gated at component level) */}
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}

export default B2BApp
